/**
 * Sprint P4 – Subscription & Billing
 * Sprint P5 – Usage Tracking
 * Sprint P6 – Feature Gating
 */
import { useState, useEffect } from 'react';
import { companiesService, featureFlagService, plansService } from '@/modules/admin/companies/services/companies.service';
import type { SubscriptionPlan, FeatureFlag, Company } from '@/modules/admin/companies/types';
import { PLAN_CONFIG, FEATURE_LABELS } from '@/modules/admin/companies/types';
import { EmptyState } from '@/components/ui/EmptyState';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';

type ActiveTab = 'plans' | 'usage' | 'feature_flags';

// ── Subscription Plans Tab ─────────────────────────────────────
function PlansTab({ plans }: { plans: SubscriptionPlan[] }) {
  const formatPrice = (n: number) =>
    n === 0 ? 'Miễn phí' : `${(n / 1000000).toFixed(1)}M đ`;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-base font-semibold text-slate-800 mb-1">Các gói đăng ký</h2>
        <p className="text-sm text-slate-500">So sánh tính năng và giới hạn theo từng gói SaaS</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map(plan => {
          const cfg = PLAN_CONFIG[plan.code];
          const isUnlimited = plan.max_projects === -1;
          return (
            <div key={plan.code} className={`rounded-2xl border-2 p-5 ${cfg.border} ${cfg.bg} relative`}>
              {plan.code === 'pro' && (
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-green-600 text-white text-xs font-bold px-3 py-0.5 rounded-full whitespace-nowrap">
                  Phổ biến nhất
                </div>
              )}
              <div className="mb-4">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${cfg.badge}`}>{cfg.label}</span>
                <p className="text-2xl font-black mt-3 text-slate-900">{formatPrice(plan.price_monthly)}</p>
                <p className="text-xs text-slate-400 mt-0.5">/tháng</p>
                {plan.price_yearly > 0 && (
                  <p className="text-xs text-green-600 mt-1">
                    <i className="ri-percent-line mr-0.5" />
                    {formatPrice(plan.price_yearly)}/năm (tiết kiệm {Math.round((1 - plan.price_yearly / (plan.price_monthly * 12)) * 100)}%)
                  </p>
                )}
              </div>
              <p className="text-xs text-slate-500 mb-4">{plan.description}</p>
              <div className="space-y-2 mb-4">
                {[
                  { label: 'Dự án', value: isUnlimited ? 'Không giới hạn' : `${plan.max_projects} dự án` },
                  { label: 'Người dùng', value: isUnlimited ? 'Không giới hạn' : `${plan.max_users} users` },
                  { label: 'Tài sản', value: isUnlimited ? 'Không giới hạn' : `${plan.max_assets.toLocaleString('vi-VN')} tài sản` },
                ].map(item => (
                  <div key={item.label} className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">{item.label}</span>
                    <span className="font-semibold text-slate-700">{item.value}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-1.5 border-t border-slate-200 pt-3">
                {plan.features.slice(0, 6).map(fk => {
                  const fl = FEATURE_LABELS[fk];
                  return (
                    <div key={fk} className="flex items-center gap-1.5 text-xs text-slate-600">
                      <span className="w-3.5 h-3.5 flex items-center justify-center text-green-500">
                        <i className="ri-checkbox-circle-fill text-xs" />
                      </span>
                      <i className={`${fl?.icon ?? 'ri-checkbox-line'} text-slate-400 text-xs`} />
                      {fl?.label ?? fk}
                    </div>
                  );
                })}
                {plan.features.length > 6 && (
                  <p className="text-xs text-slate-400 pl-5">+{plan.features.length - 6} tính năng khác</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Usage Tracking Tab ─────────────────────────────────────────
function UsageTab({ companies }: { companies: Company[] }) {
  const [selectedCompany, setSelectedCompany] = useState(companies[0]?.id ?? '');
  const [usageHistory, setUsageHistory] = useState<Array<{ period_date: string;[key: string]: number | string }>>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!selectedCompany) return;
    setIsLoading(true);
    companiesService.getUsageHistory(selectedCompany).then(res => {
      setUsageHistory(res.data ?? []);
      setIsLoading(false);
    });
  }, [selectedCompany]);

  const selectedCo = companies.find(c => c.id === selectedCompany);

  const chartData = usageHistory.map(d => ({
    month: new Date(d.period_date as string).toLocaleDateString('vi-VN', { month: 'short', year: '2-digit' }),
    'Dự án': d.projects_count ?? 0,
    'Users': d.users_count ?? 0,
    'Tài sản': d.assets_count ?? 0,
    'Work Orders': d.work_orders_count ?? 0,
  }));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-800 mb-1">Theo dõi mức sử dụng</h2>
          <p className="text-sm text-slate-500">Sprint P5 – Usage tracking theo công ty / tháng</p>
        </div>
        <select value={selectedCompany} onChange={e => setSelectedCompany(e.target.value)}
          className="h-9 px-3 text-sm border border-slate-200 rounded-lg bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500">
          {companies.map(c => <option key={c.id} value={c.id}>[{c.code}] {c.name}</option>)}
        </select>
      </div>

      {selectedCo && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3 flex-wrap">
          <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-lg">
            <i className="ri-building-4-line text-green-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800">{selectedCo.name}</p>
            <p className="text-xs text-slate-500">Gói: <span className={`font-medium ${PLAN_CONFIG[selectedCo.subscription_plan_code].text}`}>{PLAN_CONFIG[selectedCo.subscription_plan_code].label}</span></p>
          </div>
          <div className="ml-auto flex gap-4 text-xs text-slate-600">
            <span>Giới hạn: <strong>{selectedCo.max_projects === -1 ? '∞' : selectedCo.max_projects}</strong> dự án</span>
            <span><strong>{selectedCo.max_users === -1 ? '∞' : selectedCo.max_users}</strong> users</span>
            <span><strong>{selectedCo.max_assets === -1 ? '∞' : selectedCo.max_assets.toLocaleString('vi-VN')}</strong> tài sản</span>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="h-64 bg-slate-50 rounded-xl animate-pulse" />
      ) : usageHistory.length === 0 ? (
        <EmptyState icon="ri-bar-chart-2-line" title="Chưa có dữ liệu sử dụng" description="Dữ liệu usage sẽ được theo dõi mỗi tháng." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="text-sm font-semibold text-slate-700 mb-4">Tài nguyên theo tháng</p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Dự án" fill="#22c55e" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Users" fill="#f59e0b" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="text-sm font-semibold text-slate-700 mb-4">Work Orders & Tài sản theo tháng</p>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="Tài sản" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="Work Orders" stroke="#f97316" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Feature Flags Tab ─────────────────────────────────────────
function FeatureFlagsTab({ flags, onToggle }: { flags: FeatureFlag[]; onToggle: (planCode: string, featureKey: string, enabled: boolean) => void }) {
  const plans = ['free', 'basic', 'pro', 'enterprise'] as const;
  const allFeatureKeys = [...new Set(flags.map(f => f.feature_key))].sort();

  const flagMap: Record<string, Record<string, boolean>> = {};
  flags.forEach(f => {
    if (!flagMap[f.plan_code]) flagMap[f.plan_code] = {};
    flagMap[f.plan_code][f.feature_key] = f.is_enabled;
  });

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-base font-semibold text-slate-800 mb-1">Feature Gating</h2>
        <p className="text-sm text-slate-500">Sprint P6 – Bật/tắt tính năng theo gói đăng ký</p>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-4 py-3 sm:text-sm text-xs font-normal text-slate-500 min-w-[220px]">Tính năng</th>
              {plans.map(p => (
                <th key={p} className="px-4 py-3 text-center sm:text-sm text-xs font-normal">
                  <span className={`px-2 py-0.5 rounded-full border ${PLAN_CONFIG[p].badge}`}>{PLAN_CONFIG[p].label}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {allFeatureKeys.map(fk => {
              const fl = FEATURE_LABELS[fk];
              return (
                <tr key={fk} className="hover:bg-slate-50/60">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="w-7 h-7 flex items-center justify-center bg-slate-100 rounded-lg">
                        <i className={`${fl?.icon ?? 'ri-checkbox-line'} text-slate-500 text-sm`} />
                      </span>
                      <div>
                        <p className="text-sm font-medium text-slate-700">{fl?.label ?? fk}</p>
                        <p className="text-xs text-slate-400 font-mono">{fk}</p>
                      </div>
                    </div>
                  </td>
                  {plans.map(p => {
                    const isEnabled = flagMap[p]?.[fk] ?? false;
                    return (
                      <td key={p} className="px-4 py-3 text-center">
                        <button
                          onClick={() => onToggle(p, fk, !isEnabled)}
                          className={`w-9 h-5 rounded-full transition-colors cursor-pointer relative ${isEnabled ? 'bg-green-500' : 'bg-slate-200'}`}
                          title={isEnabled ? 'Đang bật – click để tắt' : 'Đang tắt – click để bật'}
                        >
                          <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-all ${isEnabled ? 'left-4' : 'left-0.5'}`} />
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export default function SubscriptionPage() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('plans');
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      plansService.list(),
      featureFlagService.getAllFlags(),
      companiesService.list(),
    ]).then(([planRes, flagRes, coRes]) => {
      setPlans(planRes.data ?? []);
      setFlags(flagRes.data ?? []);
      setCompanies(coRes.data ?? []);
      setIsLoading(false);
    });
  }, []);

  const handleToggleFlag = async (planCode: string, featureKey: string, enabled: boolean) => {
    await featureFlagService.toggle(planCode, featureKey, enabled);
    setFlags(prev => prev.map(f =>
      f.plan_code === planCode && f.feature_key === featureKey ? { ...f, is_enabled: enabled } : f
    ));
  };

  const tabs: { key: ActiveTab; label: string; icon: string }[] = [
    { key: 'plans', label: 'Gói đăng ký', icon: 'ri-vip-crown-line' },
    { key: 'usage', label: 'Usage Tracking', icon: 'ri-bar-chart-2-line' },
    { key: 'feature_flags', label: 'Feature Gating', icon: 'ri-toggle-line' },
  ];

  return (
    <div className="space-y-4 px-4 py-4 md:space-y-6 md:px-6 md:py-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            Subscription & Monetization
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Sprint P4 Billing · P5 Usage · P6 Feature Gating – nền tảng SaaS thương mại</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl w-fit">
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg cursor-pointer transition-colors whitespace-nowrap ${activeTab === tab.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}>
            <i className={tab.icon} /> {tab.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 h-64 animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          {activeTab === 'plans' && <PlansTab plans={plans} />}
          {activeTab === 'usage' && <UsageTab companies={companies} />}
          {activeTab === 'feature_flags' && <FeatureFlagsTab flags={flags} onToggle={handleToggleFlag} />}
        </>
      )}
    </div>
  );
}
